import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FormRow } from '@zocdoc-frontend-common/styles-grid';
import { recordAssignment } from '@zocdoc-frontend-common/utils-ab';
import { useCustomMetrics } from '@zocdoc-frontend-common/utils-frontend-metrics';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { codeToStateUSOnly } from '@zocdoc-frontend-common/utils-state-codes-to-names';
import { UnstyledLink } from '@zocdoc-mezzanine/components-link';
import {
    labelSmall,
    paragraphSmall,
    headingExtraSmall,
} from '@zocdoc-mezzanine/foundations-typography';
import { Banner } from 'src/Booking/components/Banner';
import { OUT_OF_STATE_VIDEO_VISIT } from 'src/config/ab/experiments.js';
import { shouldShowOutOfStateVideoVisitBanner } from '../../../config/ab/selector';

/**
 *  @param {string} state - State name for current selected provider location
 *  @param {object} professional - Professional(provider) object
 *  @param {string} procedureId - Procedure Id for current booking
 *  @param {string} insuranceCarrierId - Insurance carrier Id
 *  @param {string} insurancePlanId - Insurance plan id
 *  @return {JSX.Element}
 */
export const VideoVisitStateRequirementBanner = ({
    state,
    professional,
    procedureId,
    insuranceCarrierId,
    insurancePlanId,
}) => {
    const stateFullName = state ? codeToStateUSOnly[state] : '';
    const sendMetric = useCustomMetrics({
        component: 'VideoVisitStateRequirementBanner',
        section: 'StateRequirementBanner',
    });
    const showOutOfStateVideoVisitBanner = useSelector(
        shouldShowOutOfStateVideoVisitBanner
    );

    const queryParamsToCaptureMetric = `/search?referrerType=ReviewAndBook&component=VideoVisitStateRequirementBanner&section=StateRequirementBanner`;

    const queryParamsToSearch = `reason_visit=${procedureId}&dr_specialty=${professional?.specialtyId}&insurance_carrier=${insuranceCarrierId}&insurance_plan=${insurancePlanId}`;

    const searchUrl = `${queryParamsToCaptureMetric}&${queryParamsToSearch}`;

    useEffect(() => {
        if (state) {
            recordAssignment(OUT_OF_STATE_VIDEO_VISIT);

            if (showOutOfStateVideoVisitBanner) {
                sendMetric({
                    initiator: EVENT_INITIATORS.USER,
                    interactionType: INTERACTION_TYPES.VIEW,
                    action: 'View out of state banner on review and book',
                });
            }
        }
    }, [sendMetric, state, showOutOfStateVideoVisitBanner]);

    if (state && showOutOfStateVideoVisitBanner) {
        const textContent = (
            <VideoVisitBannerWrapper data-test="video-visit-banner-wrapper">
                <VideoVisitBannerHeading data-test="video-visit-banner-heading">
                    Video Visit in {stateFullName}
                </VideoVisitBannerHeading>

                <VideoVisitBannerText data-test="video-visit-banner-text">
                    You must be in {<StateName>{stateFullName}</StateName>}{' '}
                    during your appointment. Providers can only treat patients
                    in states where they are licensed.
                </VideoVisitBannerText>

                <StyledLink data-test="search-page-link" href={searchUrl}>
                    Need to find a different provider?
                </StyledLink>
            </VideoVisitBannerWrapper>
        );

        return (
            <FormRow data-test="review-and-book-video-visit-form-row-wrapper">
                <Banner textContent={textContent}></Banner>
            </FormRow>
        );
    }
    return null;
};

const StyledLink = styled(UnstyledLink)`
    ${labelSmall}
    text-decoration: underline;
    margin-top: 12px;
`;

const StateName = styled.span`
    ${headingExtraSmall}
`;

const VideoVisitBannerHeading = styled.div`
    ${headingExtraSmall}
    margin-bottom: 12px;
`;

const VideoVisitBannerText = styled.div`
    ${paragraphSmall}
    white-space: normal;
`;

const VideoVisitBannerWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;
